import { useEffect, useState } from 'react';
import type { ImgHTMLAttributes, ReactNode, SyntheticEvent } from 'react';
import { catalogAssetCacheKey, isProtectedCatalogAsset, resolveCatalogAsset, subscribeCatalogAssetInvalidation } from '../api/assets';

const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

export function CatalogAssetImage({ src, fallback = null, onError, ...props }: {
  src: string;
  fallback?: ReactNode;
} & Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'>) {
  const [resolved, setResolved] = useState(() => isProtectedCatalogAsset(src) ? '' : src);
  const [failed, setFailed] = useState(false);
  const [revision, setRevision] = useState(0);

  useEffect(() => subscribeCatalogAssetInvalidation((cacheKey) => {
    if (cacheKey === catalogAssetCacheKey(src)) setRevision((current) => current + 1);
  }), [src]);

  useEffect(() => {
    let current = true;
    setFailed(false);
    if (!isProtectedCatalogAsset(src)) {
      setResolved(src);
      return () => { current = false; };
    }
    setResolved('');
    void resolveCatalogAsset(src)
      .then((url) => { if (current) setResolved(url); })
      .catch(() => { if (current) setFailed(true); });
    return () => { current = false; };
  }, [revision, src]);

  if (!src || failed) return <>{fallback}</>;
  const handleError = (event: SyntheticEvent<HTMLImageElement>) => {
    setFailed(true);
    onError?.(event);
  };
  return <img {...props} src={resolved || TRANSPARENT_PIXEL} onError={handleError} />;
}
